//React Query
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

//Http Methods
import { getRequest as get } from 'api/utils/HttpMethods';
import queryString from 'api/utils/queryStringBuilder';

//Interfaces
import { Organization, isOrganization } from 'interfaces';

//Utils
import {
  createEntity,
  updateEntity,
  useListQuery,
  useMutationQuery,
} from '../utils/queries';

export const organizationQueryKeys = {
  all: ['organizations'],
  listOrganizations: (organizationID: string, isAdmin: boolean) => [
    ...organizationQueryKeys.all,
    'list',
    organizationID,
    isAdmin,
  ],
  get: (id: string) => [...organizationQueryKeys.all, id],
  listOrganizationsByProvider: (providerID: string) => [
    ...organizationQueryKeys.all,
    providerID,
  ],
};

/**
 * @description Fetches a list of all organizations.
 * @returns {Promise<Organization[]>} A promise that resolves to an array of organizations.
 */
const listOrganizations = async (): Promise<Organization[]> =>
  await get<Organization[]>('QBilling', `/resource/organization/all`);

/**
 * @description Fetches an organization by its ID.
 * @param {string} id - The ID of the organization.
 * @returns {Promise<Organization[] | null>} A promise that resolves to an array containing the organization or null.
 */
const getOrganization = async (id: string): Promise<Organization[] | null> => {
  const org = await get<Organization>(
    'QBilling',
    `/resource/organization${queryString({ id })}`
  );
  return [org]; // needs to be an array for useListQuery
};

/**
 * @description Creates a new organization.
 * @param {Organization} organization - The organization to create.
 * @returns {Promise<Organization>} A promise that resolves to the created organization.
 */
export const createOrganization = async (
  organization: Organization
): Promise<Organization> => {
  return createEntity<Organization>({
    entity: organization,
    functionName: 'createOrganization',
    isEntity: isOrganization,
    path: '/resource/organization',
  });
};

/**
 * @description Updates an existing organization.
 * @param {Organization} organization - The organization to update.
 * @returns {Promise<Organization>} A promise that resolves to the updated organization.
 */
export const updateOrganization = async (
  organization: Organization
): Promise<Organization> => {
  return updateEntity<Organization>({
    entity: organization,
    functionName: 'updateOrganization',
    isEntity: isOrganization,
    path: '/resource/organization',
  });
};

/**
 * @description Hook to fetch a list of organizations based on admin status and organization ID.
 * @param {Object} params - The parameters for the hook.
 * @param {boolean} params.isAdmin - Whether the user is an admin.
 * @param {string} params.organizationID - The ID of the organization.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<Organization[], Error>} The query result containing the list of organizations.
 */
export const useListOrganizations = ({
  isAdmin,
  organizationID,
  displayNotification = false,
}: {
  isAdmin: boolean;
  organizationID: string;
  displayNotification?: boolean;
}): UseQueryResult<Organization[], Error> => {
  const enabled = isAdmin ? true : !!organizationID;
  const params = isAdmin ? null : organizationID;

  return useListQuery<Organization>({
    queryKey: organizationQueryKeys.listOrganizations(organizationID, isAdmin),
    fetchFunction: isAdmin ? listOrganizations : getOrganization,
    functionName: isAdmin ? 'listOrganizations' : 'getOrganization',
    isEntity: isOrganization,
    params: params,
    enabled: enabled,
    displayNotification,
  });
};

/**
 * @description Fetches a list of organizations by provider ID.
 * @param {string} providerID - The ID of the provider.
 * @returns {Promise<Organization[]>} A promise that resolves to an array of organizations.
 */
const listOrganizationsByProvider = async (
  providerID: string
): Promise<Organization[]> =>
  await get<Organization[]>(
    'QBilling',
    `/resource/organization/provider${queryString({ providerID })}`
  );

/**
 * @description Hook to fetch a list of organizations by provider ID.
 * @param {Object} params - The parameters for the hook.
 * @param {string} params.providerID - The ID of the provider.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<Organization[], Error>} The query result containing the list of organizations.
 */
export const useListOrganizationsByProvider = ({
  providerID,
  displayNotification = false,
}: {
  providerID: string;
  displayNotification?: boolean;
}): UseQueryResult<Organization[], Error> => {
  return useListQuery<Organization>({
    queryKey: organizationQueryKeys.listOrganizationsByProvider(providerID),
    fetchFunction: listOrganizationsByProvider,
    functionName: 'listOrganizationsByProvider',
    params: providerID,
    isEntity: isOrganization,
    enabled: !!providerID,
    displayNotification,
  });
};

/**
 * @description Hook to fetch an organization by its ID.
 * @param {Object} params - The parameters for the hook.
 * @param {string} params.id - The ID of the organization.
 * @param {boolean} [params.displayNotification=false] - Whether to display notifications.
 * @returns {UseQueryResult<Organization[] | null, Error>} The query result containing the organization.
 */
export const useGetOrganization = ({
  id,
  displayNotification = false,
}: {
  id: string;
  displayNotification?: boolean;
}): UseQueryResult<Organization[] | null, Error> => {
  return useListQuery<Organization>({
    queryKey: organizationQueryKeys.get(id),
    fetchFunction: getOrganization,
    functionName: 'getOrganization',
    params: id,
    isEntity: isOrganization,
    enabled: !!id,
    displayNotification,
  });
};

/**
 * @description Hook to create a new organization.
 * @returns {UseMutationResult<Organization, Error>} The mutation result for creating an organization.
 */
export const useCreateOrganization = (): UseMutationResult<
  Organization,
  Error
> => {
  return useMutationQuery<Organization>({
    mutationFn: createOrganization,
    successMessage: 'Organization created successfully',
    errorMessage: 'Error Creating Organization',
    queryKey: organizationQueryKeys.all,
  });
};

/**
 * @description Hook to update an existing organization.
 * @returns {UseMutationResult<Organization, Error>} The mutation result for updating an organization.
 */
export const useUpdateOrganization = (): UseMutationResult<
  Organization,
  Error
> => {
  return useMutationQuery<Organization>({
    mutationFn: updateOrganization,
    successMessage: 'Organization updated successfully',
    errorMessage: 'Error Updating Organization',
    queryKey: organizationQueryKeys.all,
  });
};
